import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Eyebrow } from "@/components/Eyebrow";
import { BookButton } from "@/components/BookButton";
import { cn } from "@/lib/utils";
import { RUN_RETAINER_FROM } from "@/lib/site";

const TIERS = [
    {
        name: "Diagnose.",
        price: "From $2,500",
        cadence: "one time",
        tag: null,
        body: "A deep dive on how you actually run, and a map of where AI pays off. You keep the map either way.",
        flagship: false,
    },
    {
        name: "Maintain.",
        price: `From $${RUN_RETAINER_FROM.toLocaleString()}`,
        cadence: "per month",
        tag: "Most companies land here.",
        body: "Your AI team on retainer. Every system kept live, drift fixed before you see it, new automations as you grow, and a monthly report on what it's saving you.",
        flagship: true,
    },
    {
        name: "Build.",
        price: "From $6,000",
        cadence: "per project",
        tag: null,
        body: "The working system, wired into your tools and tested on your real data. Scoped fixed, shipped fast.",
        flagship: false,
    },
];

function Tier({ tier, index }) {
    return (
        <motion.div
            data-testid={`pricing-tier-${index}`}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: index * 0.08 }}
            className={cn(
                "rounded-3xl border p-8 flex flex-col",
                tier.flagship
                    ? "border-foreground bg-foreground text-background lg:-translate-y-4 lg:pb-12"
                    : "border-border bg-background"
            )}
        >
            {tier.tag ? (
                <span
                    className={cn(
                        "inline-flex self-start items-center gap-2 text-xs font-medium mb-6 px-3 py-1 rounded-full",
                        tier.flagship ? "bg-background/15" : "bg-surface"
                    )}
                >
                    {tier.tag}
                </span>
            ) : (
                <span className="mb-6 h-[26px]" />
            )}

            <h3 className="font-display font-semibold text-2xl tracking-tight">
                {tier.name.replace(".", "")}
                <span className="period">.</span>
            </h3>

            <div className="mt-5 flex items-baseline gap-2">
                <span className="font-display font-bold text-4xl tracking-tight">{tier.price}</span>
                <span className={cn("text-sm", tier.flagship ? "text-background/70" : "text-muted-foreground")}>
                    {tier.cadence}
                </span>
            </div>

            <p className={cn("mt-6 leading-relaxed flex-1", tier.flagship ? "text-background/85" : "text-muted-foreground")}>
                {tier.body}
            </p>

            <div className="mt-8">
                <BookButton
                    testid={`pricing-book-${index}`}
                    variant={tier.flagship ? "solid" : "outline"}
                    className={cn("w-full justify-center", tier.flagship && "bg-background text-foreground hover:bg-background/90")}
                />
            </div>
        </motion.div>
    );
}

export function Pricing() {
    return (
        <section id="pricing" data-testid="pricing" className="py-20 sm:py-24 bg-surface border-y border-border">
            <div className="max-w-6xl mx-auto px-5 sm:px-8">
                <Eyebrow>Pricing</Eyebrow>
                <h2 className="mt-5 max-w-3xl font-display font-semibold tracking-[-0.02em] text-4xl sm:text-5xl leading-[1.08]">
                    One ladder. Climb as far as it earns<span className="period">.</span>
                </h2>

                <div className="mt-16 grid lg:grid-cols-3 gap-5 lg:items-center">
                    {TIERS.map((t, i) => (
                        <Tier key={i} tier={t} index={i} />
                    ))}
                </div>

                <p className="mt-10 max-w-2xl text-lg text-foreground/90 leading-relaxed">
                    A full AI team, for less than the salary of the one person you'd hire to figure
                    this out.
                </p>
            </div>
        </section>
    );
}
